import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  POLLY_STANDARD_LANGUAGE_OPTIONS,
  getPollyLanguageName
} from '../../constants/pollyStandardVoices';
import { usePlan } from '../../hooks/usePlan';
import { openPricingModal } from '../../utils/pricingModal';
import './sourceWizard.css';

/*
 * SourceWizard (T4) — a popout modal that adds a single news source through
 * one decision per step:
 *   1. Where should news come from?  (📚 Popular presets / 🔗 Custom — Plus)
 *   2a. Preset gallery (region/topic grouped) — no category question ever
 *   2b. Custom source (name + URL + category + source language)
 *   3. Learn a language with this source? (English-only default first)
 *   4. Confirm + Add (reuses the parent's existing save path)
 *
 * Props:
 *   presets         — array of sample sources (raw category incl. general-*)
 *   existingSources — already-saved sources (to disable added presets)
 *   onSave(data)    — async; persists { name, url, category, sourceLanguage,
 *                     translationLanguage }. Throws on validation/save error.
 *   onClose()       — close the wizard (called after a successful save, or on
 *                     ✕/Back-from-step-1 with confirm-if-dirty)
 */

// Region/topic grouping for the preset gallery. `key` matches the raw preset
// category; general-* keys are grouped by region but heal to plain 'general'
// on the saved record (see healCategory).
const PRESET_GROUPS = [
  { key: 'general-na', label: 'North America', emoji: '🇺🇸' },
  { key: 'general-eu', label: 'Europe', emoji: '🇪🇺' },
  { key: 'general-apac', label: 'Asia Pacific', emoji: '🌏' },
  { key: 'general-intl', label: 'International', emoji: '🌐' },
  { key: 'technology', label: 'Technology', emoji: '💻' },
  { key: 'business', label: 'Business & Finance', emoji: '💼' },
  { key: 'sports', label: 'Sports', emoji: '⚽' }
];

const CATEGORY_LABELS = {
  general: 'General',
  technology: 'Technology',
  business: 'Business',
  sports: 'Sports',
  health: 'Health',
  science: 'Science',
  entertainment: 'Entertainment'
};

const SOURCE_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' }
];

const sourceLanguageLabel = (code) =>
  (SOURCE_LANGUAGE_OPTIONS.find((o) => o.value === code) || {}).label || code;

// Step 3's combobox options: language name only (no voice names in the UI —
// voice selection happens later, in the scheduler's HostVoicePicker). A
// language with no Polly voice mapping is filtered out here rather than left
// selectable and erroring after the fact; today every entry in
// POLLY_STANDARD_LANGUAGE_OPTIONS carries a voiceId, so this is a defensive
// guard against future map edits. Sorted alphabetically by display name.
const TRANSLATION_LANGUAGE_OPTIONS = POLLY_STANDARD_LANGUAGE_OPTIONS
  .filter((opt) => Boolean(opt.voiceId))
  .slice()
  .sort((a, b) => a.languageName.localeCompare(b.languageName));

const healCategory = (category) =>
  typeof category === 'string' && category.startsWith('general-') ? 'general' : category;

function SourceWizard({ presets = [], existingSources = [], onSave, onClose }) {
  const { plan } = usePlan();
  const isFree = plan === 'free';

  const [step, setStep] = useState(1);
  const [path, setPath] = useState(null); // 'preset' | 'custom'
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [custom, setCustom] = useState({
    name: '',
    url: '',
    category: 'general',
    sourceLanguage: 'en'
  });
  const [translationLanguage, setTranslationLanguage] = useState(''); // '' = English only
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Step 3 language combobox (search input + filtered listbox).
  const [langQuery, setLangQuery] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
  const langListboxRef = useRef(null);

  const filteredLanguageOptions = useMemo(() => {
    const q = langQuery.trim().toLowerCase();
    if (!q) return TRANSLATION_LANGUAGE_OPTIONS;
    return TRANSLATION_LANGUAGE_OPTIONS.filter((opt) =>
      opt.languageName.toLowerCase().includes(q)
    );
  }, [langQuery]);

  // Keep the highlighted option scrolled into view as the user arrows through it.
  useEffect(() => {
    if (!langOpen || activeOptionIndex < 0 || !langListboxRef.current) {
      return;
    }
    const activeEl = langListboxRef.current.children[activeOptionIndex];
    if (activeEl && typeof activeEl.scrollIntoView === 'function') {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [activeOptionIndex, langOpen]);

  const addedUrls = new Set(
    existingSources.map((s) => (s.url || '').trim().toLowerCase())
  );
  const isPresetAdded = (preset) => addedUrls.has((preset.url || '').trim().toLowerCase());

  const isDirty =
    path !== null || !!selectedPreset || !!custom.name || !!custom.url || !!translationLanguage;

  const requestClose = () => {
    if (isDirty && !window.confirm('Discard this news source and close?')) {
      return;
    }
    onClose();
  };

  // The source data assembled from whichever path is active.
  const draft =
    path === 'preset' && selectedPreset
      ? {
          name: selectedPreset.name,
          url: selectedPreset.url,
          category: healCategory(selectedPreset.category),
          sourceLanguage: selectedPreset.sourceLanguage || 'en',
          translationLanguage
        }
      : {
          name: custom.name.trim(),
          url: custom.url.trim(),
          category: custom.category,
          sourceLanguage: custom.sourceLanguage,
          translationLanguage
        };

  // Custom-form validation (mirrors the parent save path so Next gates cleanly).
  const validateCustom = () => {
    if (!custom.name.trim() || !custom.url.trim()) {
      return 'Please fill in both name and URL';
    }
    if (
      custom.url.toLowerCase().includes('reuters.com') ||
      custom.name.toLowerCase().includes('reuters')
    ) {
      return '❌ Reuters is not available due to aggressive bot protection (DataDome). Please use The Guardian or NPR instead.';
    }
    try {
      // eslint-disable-next-line no-new
      new URL(custom.url.trim());
    } catch (e) {
      return 'Please enter a valid URL (e.g., https://example.com)';
    }
    return null;
  };

  const goToStep = (next) => {
    setError(null);
    setStep(next);
  };

  const choosePreset = () => {
    setPath('preset');
    setShowUpgrade(false);
    goToStep(2);
  };

  const chooseCustom = () => {
    if (isFree) {
      setShowUpgrade(true);
      return;
    }
    setPath('custom');
    goToStep(2);
  };

  const selectPreset = (preset) => {
    setSelectedPreset(preset);
    goToStep(3);
  };

  const submitCustom = () => {
    const validationError = validateCustom();
    if (validationError) {
      setError(validationError);
      return;
    }
    goToStep(3);
  };

  const selectLanguage = (code) => {
    setTranslationLanguage(code);
    goToStep(4);
  };

  // Selecting from the combobox (click or Enter) mirrors selectLanguage's
  // auto-advance behavior, plus resets the combobox's own UI state.
  const selectLanguageOption = (opt) => {
    setLangQuery(opt.languageName);
    setLangOpen(false);
    setActiveOptionIndex(-1);
    selectLanguage(opt.languageCode);
  };

  const handleLangQueryChange = (e) => {
    setLangQuery(e.target.value);
    setLangOpen(true);
    setActiveOptionIndex(0);
  };

  const handleLangKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!langOpen) {
        setLangOpen(true);
        setActiveOptionIndex(filteredLanguageOptions.length > 0 ? 0 : -1);
        return;
      }
      setActiveOptionIndex((prev) => Math.min(prev + 1, filteredLanguageOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!langOpen) {
        setLangOpen(true);
        setActiveOptionIndex(filteredLanguageOptions.length > 0 ? 0 : -1);
        return;
      }
      setActiveOptionIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (langOpen && activeOptionIndex >= 0 && filteredLanguageOptions[activeOptionIndex]) {
        e.preventDefault();
        selectLanguageOption(filteredLanguageOptions[activeOptionIndex]);
      }
    } else if (e.key === 'Escape') {
      if (langOpen) {
        e.preventDefault();
        setLangOpen(false);
        setActiveOptionIndex(-1);
      }
    }
  };

  const handleAdd = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      onClose();
    } catch (e) {
      setError(e.message || 'Could not add this source. Please try again.');
      setSaving(false);
    }
  };

  const back = () => {
    setError(null);
    if (step === 1) {
      requestClose();
    } else if (step === 2) {
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    } else {
      setStep(3);
    }
  };

  // ---- Step renderers ----------------------------------------------------
  const renderStep1 = () => (
    <div>
      <h3 className="source-wizard__step-title">Where should your news come from?</h3>
      <div className="sw-option-cards">
        <button type="button" className="sw-option-card" onClick={choosePreset}>
          <span className="sw-option-emoji">📚</span>
          <span className="sw-option-title">Popular sources</span>
          <span className="sw-option-desc">
            Pick from trusted news outlets — added in a couple of clicks.
          </span>
        </button>
        <button
          type="button"
          className={`sw-option-card${isFree ? ' sw-option-card--locked' : ''}`}
          onClick={chooseCustom}
        >
          <span className="sw-option-emoji">{isFree ? '🔒' : '🔗'}</span>
          <span className="sw-option-title">Custom website</span>
          {isFree ? (
            <span className="sw-badge">Plus feature</span>
          ) : (
            <span className="sw-badge">Plus</span>
          )}
          <span className="sw-option-desc">
            Add any site by name and URL.
          </span>
        </button>
      </div>
      {showUpgrade && (
        <div className="sw-upgrade-note">
          🔒 Custom websites are a <strong>Plus</strong> feature.{' '}
          <button type="button" className="sw-upgrade-note__link" onClick={openPricingModal}>
            Upgrade to Plus
          </button>{' '}
          to add any site by URL — for now, choose from Popular sources.
        </div>
      )}
    </div>
  );

  const renderStep2a = () => (
    <div>
      <h3 className="source-wizard__step-title">Pick a popular source</h3>
      <p className="source-wizard__step-hint">
        Selecting one fills in everything for you — no category to choose.
      </p>
      {PRESET_GROUPS.map((group) => {
        const items = presets.filter((p) => p.category === group.key);
        if (items.length === 0) {
          return null;
        }
        return (
          <div key={group.key} className="sw-group">
            <h4 className="sw-group-heading">
              {group.emoji} {group.label}
            </h4>
            <div className="sw-preset-grid">
              {items.map((preset) => {
                const added = isPresetAdded(preset);
                return (
                  <button
                    key={preset.url}
                    type="button"
                    className={`sw-preset-card${added ? ' sw-preset-card--added' : ''}`}
                    onClick={() => !added && selectPreset(preset)}
                    disabled={added}
                    aria-label={added ? `${preset.name} (already added)` : `Add ${preset.name}`}
                  >
                    <span className="sw-preset-emoji">{group.emoji}</span>
                    <span className="sw-preset-name">{preset.name}</span>
                    {added && <span className="sw-preset-check">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderStep2b = () => (
    <div>
      <h3 className="source-wizard__step-title">Add a custom source</h3>
      <div className="form-group">
        <label htmlFor="sw-name">News source name *</label>
        <input
          id="sw-name"
          type="text"
          value={custom.name}
          onChange={(e) => setCustom({ ...custom, name: e.target.value })}
          placeholder="e.g., BBC News"
        />
      </div>
      <div className="form-group">
        <label htmlFor="sw-url">Website URL *</label>
        <input
          id="sw-url"
          type="url"
          value={custom.url}
          onChange={(e) => setCustom({ ...custom, url: e.target.value })}
          placeholder="https://example.com"
        />
      </div>
      <div className="form-group">
        <label htmlFor="sw-category">Category</label>
        <select
          id="sw-category"
          value={custom.category}
          onChange={(e) => setCustom({ ...custom, category: e.target.value })}
        >
          <option value="general">General News</option>
          <option value="technology">Technology</option>
          <option value="business">Business</option>
          <option value="sports">Sports</option>
          <option value="health">Health</option>
          <option value="science">Science</option>
          <option value="entertainment">Entertainment</option>
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="sw-source-language">Source language</label>
        <select
          id="sw-source-language"
          value={custom.sourceLanguage}
          onChange={(e) => setCustom({ ...custom, sourceLanguage: e.target.value })}
        >
          {SOURCE_LANGUAGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const activeOption =
      langOpen && activeOptionIndex >= 0 ? filteredLanguageOptions[activeOptionIndex] : null;

    return (
      <div>
        <h3 className="source-wizard__step-title">Learn a language with this source?</h3>
        <p className="source-wizard__step-hint">
          Add a second language and each episode is also read aloud in it. Otherwise, English only.
        </p>

        <button
          type="button"
          className={`sw-lang-primary${
            translationLanguage === '' ? ' sw-lang-primary--selected' : ''
          }`}
          onClick={() => selectLanguage('')}
        >
          <span className="sw-lang-name">No thanks — English only</span>
          {translationLanguage === '' && <span className="sw-preset-check">✓</span>}
        </button>

        <div className="sw-lang-combobox">
          <label htmlFor="sw-lang-search" className="sw-lang-combobox__label">
            Or search for a language
          </label>
          <input
            id="sw-lang-search"
            type="text"
            role="combobox"
            aria-expanded={langOpen}
            aria-controls="sw-lang-listbox"
            aria-autocomplete="list"
            aria-activedescendant={activeOption ? `sw-lang-option-${activeOption.languageCode}` : undefined}
            className="sw-lang-combobox__input"
            value={langQuery}
            onChange={handleLangQueryChange}
            onFocus={() => setLangOpen(true)}
            onKeyDown={handleLangKeyDown}
            onBlur={() => setLangOpen(false)}
            placeholder="Search language…"
            autoComplete="off"
          />
          {langOpen && (
            <ul
              id="sw-lang-listbox"
              role="listbox"
              aria-label="Languages"
              className="sw-lang-options"
              ref={langListboxRef}
            >
              {filteredLanguageOptions.length === 0 ? (
                <li className="sw-lang-option sw-lang-option--empty">
                  No languages match &quot;{langQuery}&quot;
                </li>
              ) : (
                filteredLanguageOptions.map((opt, idx) => (
                  <li
                    key={opt.languageCode}
                    id={`sw-lang-option-${opt.languageCode}`}
                    role="option"
                    aria-selected={translationLanguage === opt.languageCode}
                    className={`sw-lang-option${
                      idx === activeOptionIndex ? ' sw-lang-option--active' : ''
                    }${translationLanguage === opt.languageCode ? ' sw-lang-option--selected' : ''}`}
                    onMouseDown={(e) => {
                      // preventDefault keeps focus on the input so onBlur
                      // doesn't fire before the click is handled.
                      e.preventDefault();
                      selectLanguageOption(opt);
                    }}
                    onMouseEnter={() => setActiveOptionIndex(idx)}
                  >
                    {opt.languageName}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderStep4 = () => (
    <div>
      <h3 className="source-wizard__step-title">Confirm &amp; add</h3>
      <div className="sw-confirm-card">
        <div className="sw-confirm-summary">
          {draft.name} · {CATEGORY_LABELS[draft.category] || draft.category} ·{' '}
          {sourceLanguageLabel(draft.sourceLanguage)}
          {draft.translationLanguage
            ? ` → ${getPollyLanguageName(draft.translationLanguage)} (${draft.translationLanguage})`
            : ''}
        </div>
        <div className="sw-confirm-row">
          <span className="sw-confirm-label">Source</span>
          <span>{draft.url}</span>
        </div>
        <div className="sw-confirm-row">
          <span className="sw-confirm-label">Category</span>
          <span>{CATEGORY_LABELS[draft.category] || draft.category}</span>
        </div>
        <div className="sw-confirm-row">
          <span className="sw-confirm-label">Language</span>
          <span>
            {sourceLanguageLabel(draft.sourceLanguage)}
            {draft.translationLanguage
              ? ` + ${getPollyLanguageName(draft.translationLanguage)}`
              : ' only'}
          </span>
        </div>
      </div>
    </div>
  );

  const renderBody = () => {
    if (step === 1) return renderStep1();
    if (step === 2) return path === 'custom' ? renderStep2b() : renderStep2a();
    if (step === 3) return renderStep3();
    return renderStep4();
  };

  return (
    <div className="source-wizard__overlay" role="dialog" aria-modal="true" aria-label="Add news source">
      <div className="source-wizard">
        <div className="source-wizard__header">
          <h2 className="source-wizard__title">➕ Add news source</h2>
          <button
            type="button"
            className="source-wizard__close"
            aria-label="Close"
            onClick={requestClose}
          >
            ✕
          </button>
        </div>

        <div className="source-wizard__progress">
          <div className="source-wizard__dots">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`source-wizard__dot${s <= step ? ' source-wizard__dot--active' : ''}`}
              />
            ))}
          </div>
          <div className="source-wizard__step-label">Step {step} of 4</div>
        </div>

        <div className="source-wizard__body">
          {renderBody()}
          {error && <div className="error">{error}</div>}
        </div>

        <div className="source-wizard__footer">
          <button type="button" className="btn btn-secondary" onClick={back} disabled={saving}>
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step === 2 && path === 'custom' && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={submitCustom}
              disabled={!custom.name.trim() || !custom.url.trim()}
            >
              Next
            </button>
          )}

          {step === 4 && (
            <button type="button" className="btn btn-primary" onClick={handleAdd} disabled={saving}>
              {saving ? 'Adding…' : '➕ Add source'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SourceWizard;
